import React, { createContext, useContext, useState, useEffect } from 'react';
import { MenuItem, MenuItemVariant } from '@/types/index';
import { useMetaPixel } from '@/hooks/useMetaPixel';
import { Branch } from '@/hooks/useBranches';

export interface CartItem {
  menuItem: MenuItem;
  variant?: MenuItemVariant;
  quantity: number;
  price: number;
  notes?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (menuItem: MenuItem, variant?: MenuItemVariant, quantity?: number, notes?: string) => void;
  removeItem: (menuItemId: string, variantId?: string) => void;
  updateQuantity: (menuItemId: string, quantity: number, variantId?: string) => void;
  clearCart: () => void;
  totalItems: number;
  totalAmount: number;
  isInCart: (menuItemId: string, variantId?: string) => boolean;
  initiateCheckout: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    // Return a dummy context to prevent crashes in builder/preview modes
    // where CartProvider might not be present
    return {
      items: [],
      addItem: () => console.warn('Cart context missing - addItem ignored'),
      removeItem: () => console.warn('Cart context missing - removeItem ignored'),
      updateQuantity: () => console.warn('Cart context missing - updateQuantity ignored'),
      clearCart: () => console.warn('Cart context missing - clearCart ignored'),
      totalItems: 0,
      totalAmount: 0,
      isInCart: () => false,
      initiateCheckout: () => console.warn('Cart context missing - initiateCheckout ignored'),
    };
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode; branch: Branch | null }> = ({ children, branch }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  // Scope cart per branch to prevent cross-restaurant contamination
  const cartKey = branch?.id ? `restaurant-cart-${branch.id}` : 'restaurant-cart'

  // Stabilize the MetaPixel hook to prevent re-initialization on branch updates
  const [stablePixelConfig, setStablePixelConfig] = useState({
    pixelId: branch?.meta_pixel_id,
    enabled: branch?.meta_pixel_enabled,
    settings: branch?.meta_pixel_settings && typeof branch.meta_pixel_settings === 'string' 
      ? JSON.parse(branch.meta_pixel_settings) 
      : branch?.meta_pixel_settings,
  });

  // Only update pixel config when branch ID changes, not on every branch update
  useEffect(() => {
    if (branch?.id) {
      const pixelSettings = branch?.meta_pixel_settings && typeof branch.meta_pixel_settings === 'string' 
        ? JSON.parse(branch.meta_pixel_settings) 
        : branch?.meta_pixel_settings;
      
      setStablePixelConfig({
        pixelId: branch?.meta_pixel_id,
        enabled: branch?.meta_pixel_enabled,
        settings: pixelSettings,
      });
    }
  }, [branch?.id, branch?.meta_pixel_id, branch?.meta_pixel_enabled]); // Only depend on specific IDs, not the whole branch

  const { trackAddToCart, trackInitiateCheckout } = useMetaPixel(stablePixelConfig);

  // Load cart from localStorage when branch changes
  useEffect(() => {
    // Migrate/clear legacy global cart key once
    const legacy = localStorage.getItem('restaurant-cart')
    if (legacy && cartKey !== 'restaurant-cart') {
      try {
        // Do not carry over items across branches; clear legacy to avoid cross-branch orders
        localStorage.removeItem('restaurant-cart')
      } catch {}
    }

    const savedCart = localStorage.getItem(cartKey)
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart)
        setItems(parsedCart)
      } catch (error) {
        console.error('Error loading cart from localStorage:', error)
        setItems([])
      }
    } else {
      setItems([])
    }
  }, [cartKey]);

  // Save cart to localStorage whenever items change (scoped per branch)
  useEffect(() => {
    if (!cartKey) return
    if (items.length > 0) localStorage.setItem(cartKey, JSON.stringify(items))
    else localStorage.removeItem(cartKey)
  }, [items, cartKey]);

  const addItem = (menuItem: MenuItem, variant?: MenuItemVariant, quantity = 1, notes = '') => {
    const price = variant ? variant.price : menuItem.base_price;
    setItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(item => item.menuItem.id === menuItem.id && (!variant || item.variant?.id === variant.id));
      
      if (existingItemIndex >= 0) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += quantity;
        if (notes) {
          updatedItems[existingItemIndex].notes = notes;
        }
        updatedItems[existingItemIndex].price = price;
        return updatedItems;
      } else {
        const newItems = [...prevItems, { menuItem, variant, quantity, price, notes }];
        trackAddToCart({
            content_type: 'product',
            content_ids: [menuItem.id],
            content_name: menuItem.name,
            value: price * quantity,
            currency: branch?.currency || 'USD',
            contents: [{ id: menuItem.id, quantity: quantity, item_price: price }]
        });
        return newItems;
      }
    });
  };

  const removeItem = (menuItemId: string, variantId?: string) => {
    setItems(prevItems => prevItems.filter(item => item.menuItem.id !== menuItemId || (variantId && item.variant?.id !== variantId)));
  };

  const updateQuantity = (menuItemId: string, quantity: number, variantId?: string) => {
    if (quantity <= 0) {
      removeItem(menuItemId, variantId);
      return;
    }
    
    setItems(prevItems =>
      prevItems.map(item =>
        item.menuItem.id === menuItemId && (!variantId || item.variant?.id === variantId)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const initiateCheckout = () => {
    trackInitiateCheckout({
      content_type: 'product',
      content_ids: items.map(item => item.menuItem.id),
      value: totalAmount,
      currency: branch?.currency || 'USD',
      num_items: totalItems,
      contents: items.map(item => ({
        id: item.menuItem.id,
        quantity: item.quantity,
        item_price: item.menuItem.base_price,
      })),
    });
    // Add navigation to checkout page here if needed
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const isInCart = (menuItemId: string, variantId?: string) => {
    return items.some(item => item.menuItem.id === menuItemId && (!variantId || item.variant?.id === variantId));
  };

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      totalItems,
      totalAmount,
      isInCart,
      initiateCheckout,
    }}>
      {children}
    </CartContext.Provider>
  );
};
